// Substitution Management — Dashboard Statistics API
// Provides stats for the SubstitutionWidget on the dashboard

/// <reference lib="dom" />

import { handleApiCall } from './errors'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

export interface SubstitutionStats {
  teachersAbsentToday: number
  periodsCoveredToday: number
  periodsUnassignedToday: number
  absencesPendingTomorrow: number
}

/**
 * Get substitution statistics for the dashboard widget.
 * Requirements: 23.1–23.4
 */
export async function getSubstitutionStats(
  accessToken: string,
  schoolId: string
): Promise<SubstitutionStats> {
  return handleApiCall(async () => {
    const today = new Date().toISOString().split('T')[0]
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    // Build query URLs
    // Teachers absent today: count absences where today is within [start_date, end_date] and status != 'cancelled'
    const absencesTodayUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/absences?school_id=eq.${schoolId}&start_date=lte.${today}&end_date=gte.${today}&status=neq.cancelled&select=id`
      : `${SUPABASE_URL}/rest/v1/absences?start_date=lte.${today}&end_date=gte.${today}&status=neq.cancelled&select=id`

    // Periods covered today: count posted assignments for today with substitute_teacher_id not null
    const coveredPeriodsUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/assignments?school_id=eq.${schoolId}&date=eq.${today}&status=eq.posted&substitute_teacher_id=not.is.null&select=id`
      : `${SUPABASE_URL}/rest/v1/assignments?date=eq.${today}&status=eq.posted&substitute_teacher_id=not.is.null&select=id`

    // Periods unassigned today: count posted assignments for today with substitute_teacher_id null
    const unassignedPeriodsUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/assignments?school_id=eq.${schoolId}&date=eq.${today}&status=eq.posted&substitute_teacher_id=is.null&select=id`
      : `${SUPABASE_URL}/rest/v1/assignments?date=eq.${today}&status=eq.posted&substitute_teacher_id=is.null&select=id`

    // Absences pending tomorrow: count absences where tomorrow is within [start_date, end_date] 
    // and status is 'pending' or 'draft_pending' (not yet posted for tomorrow)
    const absencesTomorrowUrl = schoolId
      ? `${SUPABASE_URL}/rest/v1/absences?school_id=eq.${schoolId}&start_date=lte.${tomorrow}&end_date=gte.${tomorrow}&status=in.(pending,draft_pending)&select=id`
      : `${SUPABASE_URL}/rest/v1/absences?start_date=lte.${tomorrow}&end_date=gte.${tomorrow}&status=in.(pending,draft_pending)&select=id`

    // Fetch all stats in parallel
    const [absencesTodayRes, coveredPeriodsRes, unassignedPeriodsRes, absencesTomorrowRes] = await Promise.all([
      fetch(absencesTodayUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      fetch(coveredPeriodsUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      fetch(unassignedPeriodsUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
      fetch(absencesTomorrowUrl, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'count=exact',
        },
      }),
    ])

    // Extract counts from Content-Range header
    const getCount = (response: Response): number => {
      const contentRange = response.headers.get('content-range')
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/)
        if (match) return parseInt(match[1], 10)
      }
      return 0
    }

    const stats: SubstitutionStats = {
      teachersAbsentToday: getCount(absencesTodayRes),
      periodsCoveredToday: getCount(coveredPeriodsRes),
      periodsUnassignedToday: getCount(unassignedPeriodsRes),
      absencesPendingTomorrow: getCount(absencesTomorrowRes),
    }

    return stats
  })
}
